import { ArrowLeft, Globe } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { useDataSourceById } from "../api/data-sources";
import ContentContainer from "../components/ContentContainer";
import IconButton from "../components/IconButton";
import MainLayout from "../components/MainLayout";
import Separator from "../components/Separator";
import Typography from "../components/Typography";
import { appPaths } from "../Routes";
import AddActivityDataForm from "../components/AddActivityDataForm";
import { useAddActivityData } from "../api/activity-data";
import { AddActivityDataDto } from "../api/models/ActivityData";
import { errorToast, successToast } from "../toast";

const DataSourceAddActivityPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const { dataSource, isLoading } = useDataSourceById(Number(id));

    const [request, isAddingActivityData] = useAddActivityData(Number(id));

    const handleSubmit = async (values: AddActivityDataDto) => {
        const response = await request(values);

        if (!response.success) {
            errorToast(
                "There was a problem adding activity data - please try again"
            );
            return;
        }

        successToast("Activity data added");
        navigate(appPaths.activityData);
    };

    if (!id) return null;

    return (
        <MainLayout>
            <ContentContainer className="pt-14 pb-11">
                <div className="flex flex-col gap-6 sm:gap-0 sm:flex-row items-center pb-5">
                    <div className="flex items-center">
                        <IconButton
                            label="Back"
                            icon={ArrowLeft}
                            iconSize="large"
                            className="mr-3"
                            onClick={() => window.history.back()}
                        />
                        <Typography variant="headline-small" component="h1">
                            {dataSource?.locationName} {dataSource?.name} / Add
                            activity data
                        </Typography>
                    </div>
                    <div className="ml-auto flex items-center gap-2 font-semibold">
                        <Globe weight="light" size={24} />
                        <Typography
                            variant="headline-small"
                            component="span"
                            className="text-deep-ocean-20"
                        >
                            Scope {dataSource?.scope}
                        </Typography>
                    </div>
                </div>
                <Separator className="mb-8" />
                {isLoading && <>Loading...</>}
                {dataSource && (
                    <div className="max-w-xl rounded-xl bg-surface-light-1 p-6 mx-auto">
                        <div className="mb-6">
                            <Typography variant="body-large" component="p">
                                <b>Location:</b> {dataSource.locationName}
                            </Typography>
                            <Typography variant="body-large" component="p">
                                <b>Data Source:</b> {dataSource.name}
                            </Typography>
                        </div>
                        <AddActivityDataForm
                            onSubmit={handleSubmit}
                            isSubmitting={isAddingActivityData}
                            onCancel={() =>
                                navigate(appPaths.dataSourceDetail(id))
                            }
                            rawUnitsSuffix={dataSource.rawUnitsSuffix}
                        />
                    </div>
                )}
            </ContentContainer>
        </MainLayout>
    );
};

export default DataSourceAddActivityPage;
